import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * Visible CTA pill that links to the /live dashboard.
 * Pulses a status dot, shows the count of currently busy hubs (high/extreme),
 * and has a clear arrow icon so users read it as clickable.
 */
export default function LiveBusiestCTA({ className = '' }) {
  const [busyCount, setBusyCount] = useState(null);
  const [total, setTotal] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/airport/busiest-now?cache_only=true`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        // cache_only returns an empty list when nothing is cached — keep the
        // neutral "See live snapshot" label instead of "All clear"
        if (!data.airports || data.airports.length === 0) return;
        const busy = (data.airports || []).filter(
          (a) => a.crowd_level === 'high' || a.crowd_level === 'extreme'
        ).length;
        setBusyCount(busy);
        setTotal((data.airports || []).length);
      } catch {
        /* silent */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const label =
    busyCount === null
      ? 'See live snapshot'
      : busyCount > 0
      ? `${busyCount} hub${busyCount === 1 ? '' : 's'} busy now`
      : `All ${total || ''} hubs clear`;

  // On very small screens, prefer the compact label to avoid wrapping
  const compactLabel =
    busyCount === null
      ? 'Live'
      : busyCount > 0
      ? `${busyCount} busy`
      : 'All clear';

  return (
    <motion.div
      whileHover={{ y: -1 }}
      whileTap={{ scale: 0.97 }}
      className={className}
    >
      <Link
        to="/live"
        data-testid="live-busiest-link"
        className="group inline-flex items-center gap-2 sm:gap-2.5 pl-2.5 sm:pl-3 pr-1.5 sm:pr-2 py-1.5 sm:py-2 rounded-full bg-slate-900 text-white shadow-[0_4px_14px_-2px_rgba(15,23,42,0.35)] hover:shadow-[0_6px_20px_-2px_rgba(15,23,42,0.45)] hover:bg-slate-800 transition-colors duration-150 whitespace-nowrap"
      >
        {/* Live dot with pulse ring */}
        <span className="relative flex w-2 h-2">
          <span className="absolute inset-0 rounded-full bg-rose-400 opacity-75 animate-ping"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
        </span>

        <span className="text-[10px] sm:text-xs uppercase tracking-[0.14em] font-semibold text-rose-300">
          Live
        </span>

        <span className="text-xs sm:text-sm font-medium hidden sm:inline" data-testid="live-busiest-label">
          {label}
        </span>
        <span className="text-xs font-medium sm:hidden" data-testid="live-busiest-label-compact">
          {compactLabel}
        </span>

        <span className="ml-0.5 sm:ml-1 flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-white/10 group-hover:bg-white/20 transition-colors duration-150">
          <ArrowUpRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white transition-transform duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </Link>
    </motion.div>
  );
}
